import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { DesignSystem, getColor as _getColor, getSpacing, getShadow } from '../styles/designSystem';
import { ItineraryAPI, TrekDetails, TrekDay } from '../api/itinerary';
import AppHeader from './Header';
import { API_BASE_URL } from '../api/tourism';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import BookingForm from './BookingForm';
import EnquiryDialog from './EnquiryDialog';
import {
  ChevronDown,
  ChevronUp,
  ArrowUp,
  Sparkles,
  MapPin,
  Mountain,
  Route,
  Calendar,
  Clock,
  TrendingUp,
  Shield,
  Backpack,
  Heart,
  Leaf,
  Users,
  Sun,
  AlertTriangle,
  Check,
  Star,
  Footprints,
  Thermometer,
  Wind,
  Phone,
} from 'lucide-react';

// Luminous Gold palette — local override for detail page accent colors
// #CAB96E is the darkest accent shade; lighter tones tint toward white
const GOLD: Record<string, string> = {
  'primary.50': '#F8F8F6', 'primary.100': '#F4F3F0', 'primary.200': '#EEECE6',
  'primary.300': '#E6E4DA', 'primary.400': '#DEDAC9', 'primary.500': '#D4CEB3',
  'primary.600': '#CBC299', 'primary.700': '#CAB96E',
};
const getColor = (token: string) => GOLD[token] || _getColor(token);

// ─── Props ────────────────────────────────────────────────────────────────────

interface TrekDetailPageProps {
  trekId: string;
  state?: string;
  onBack: () => void;
  onNavigateTo?: (page: string, data?: any) => void;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(202, 185, 110, 0.4); }
  50% { box-shadow: 0 0 0 12px rgba(202, 185, 110, 0); }
`;

// ─── Hooks ────────────────────────────────────────────────────────────────────

const useScrollReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, isVisible };
};

const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
      setShowBackToTop(scrollTop > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return { progress, showBackToTop };
};

const useScrollSpy = (dayCount: number) => {
  const [activeDay, setActiveDay] = useState(1);
  useEffect(() => {
    if (dayCount === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const dayNum = parseInt(entry.target.id.replace('trek-day-', ''), 10);
            if (!isNaN(dayNum)) setActiveDay(dayNum);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-120px 0px -40% 0px' }
    );
    for (let i = 1; i <= dayCount; i++) {
      const el = document.getElementById(`trek-day-${i}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [dayCount]);
  return activeDay;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const JOURNEY_IMAGES = [
  `${API_BASE_URL}/api/images/curatedexperience/hamptra_trek.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/parashar_lake.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/dalhousie.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/tirthan_valley.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/key_monastery.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/shimlamall_road.jpg`,
  `${API_BASE_URL}/api/images/curatedexperience/hikkim_post_office.jpg`,
];

const getDifficultyColor = (difficulty: string) => {
  return '#CAB96E';
};

const getDifficultyBg = (difficulty: string) => {
  return 'rgba(202, 185, 110, 0.15)';
};

// ─── Reveal Wrapper ───────────────────────────────────────────────────────────

const RevealSection = styled.div<{ isVisible: boolean; delay?: number }>`
  opacity: ${(p: any) => p.isVisible ? 1 : 0};
  transform: translateY(${(p: any) => p.isVisible ? '0' : '24px'});
  transition: opacity ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.easeOut},
              transform ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.easeOut};
  transition-delay: ${(p: any) => (p.delay || 0)}ms;
`;

const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay }) => {
  const { ref, isVisible } = useScrollReveal();
  return <RevealSection ref={ref} isVisible={isVisible} delay={delay}>{children}</RevealSection>;
};

// ─── Scroll Progress ──────────────────────────────────────────────────────────

const ProgressBar = styled.div<{ width: number }>`
  position: fixed;
  top: 0; left: 0;
  height: 3px;
  width: ${(p: any) => p.width}%;
  background: linear-gradient(90deg, ${getColor('primary.700')}, ${getColor('secondary.600')});
  z-index: ${DesignSystem.zIndex.toast};
  transition: width 50ms linear;
`;

const BackToTopButton = styled.button<{ visible: boolean }>`
  position: fixed;
  bottom: ${getSpacing('xl')};
  right: ${getSpacing('xl')};
  width: 44px; height: 44px;
  border-radius: ${DesignSystem.borderRadius.full};
  background: ${getColor('primary.700')};
  color: ${getColor('neutral.white')};
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: ${getShadow('large')};
  z-index: ${DesignSystem.zIndex.sticky};
  opacity: ${(p: any) => p.visible ? 1 : 0};
  transform: translateY(${(p: any) => p.visible ? '0' : '16px'});
  pointer-events: ${(p: any) => p.visible ? 'auto' : 'none'};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  &:hover { background: ${getColor('primary.800')}; transform: translateY(-2px); }
`;

// ─── Page Shell ───────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  background: ${getColor('neutral.white')};
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: ${DesignSystem.typography.scale.body.lineHeight};
  min-height: 100vh;
  position: relative;
  overflow-x: clip;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
  width: 100%;
  @media (max-width: ${DesignSystem.breakpoints.md}) { padding: 0 ${getSpacing('lg')}; }
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────

const HeroSection = styled.div`
  padding: 0;
  position: relative;
  height: 100vh;
  display: flex; flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  /* Premium color grading overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background:
      linear-gradient(135deg, rgba(20, 184, 166, 0.08) 0%, rgba(6, 182, 212, 0.04) 50%, transparent 100%),
      radial-gradient(ellipse at 85% 90%, rgba(184, 175, 160, 0.06) 0%, transparent 60%),
      radial-gradient(circle at 15% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 40%);
    pointer-events: none;
    z-index: 1;
  }

  /* Cinematic vignette */
  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.15) 70%,
      rgba(0, 0, 0, 0.3) 100%
    );
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: ${DesignSystem.breakpoints.md}) {
    height: 100vh;
  }
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    height: 100vh;
  }
`;

const HeroBg = styled.div<{ bgImage?: string }>`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: ${(p: any) => p.bgImage
    ? `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.55) 100%), url(${p.bgImage})`
    : `linear-gradient(135deg, ${getColor('primary.800')} 0%, ${getColor('primary.600')} 100%)`};
  background-size: cover;
  background-position: center;
  will-change: transform;
  transform: translateZ(0);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${getSpacing('xl')} ${getSpacing('xl')} ${getSpacing('2xl')};
  width: 100%;
  position: relative; z-index: 2;
  text-align: center;
  display: flex; flex-direction: column; align-items: center;
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    padding: ${getSpacing('lg')} ${getSpacing('lg')} ${getSpacing('xl')};
  }
`;

const HeroTagline = styled.span`
  display: inline-block;
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: rgba(255,255,255,0.7);
  margin-bottom: ${getSpacing('lg')};
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.875rem, 4vw, ${DesignSystem.typography.scale.h1.size});
  font-weight: ${DesignSystem.typography.scale.h1.weight};
  color: #fff;
  margin: 0 0 ${getSpacing('md')};
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: ${DesignSystem.typography.scale.h1.letterSpacing};
  line-height: ${DesignSystem.typography.scale.h1.lineHeight};
  text-shadow: 0 2px 20px rgba(0,0,0,0.3);
`;

const HeroOverview = styled.p`
  font-size: 1.0rem;
  font-weight: 400;
  font-style: italic;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.85);
  margin: ${getSpacing('md')} 0 0;
  max-width: 650px;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0,0,0,0.4);
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    font-size: 0.95rem;
  }
`;

const HeroCTAButton = styled.button`
  margin-top: 24px;
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: ${DesignSystem.typography.fonts.primary};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: #CAB96E;
  border-radius: 50px;
  border: 1px solid #CAB96E;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(101, 90, 71, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: #b8a85e;
    border-color: #b8a85e;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(101, 90, 71, 0.3);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const DifficultyBadge = styled.span<{ difficulty: string }>`
  display: flex; align-items: center; gap: 6px;
  width: fit-content;
  padding: 6px 16px;
  border-radius: ${DesignSystem.borderRadius.full};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${(p: any) => getDifficultyColor(p.difficulty)};
  background: ${(p: any) => getDifficultyBg(p.difficulty)};
  backdrop-filter: blur(8px);
  border: 1px solid ${(p: any) => getDifficultyColor(p.difficulty)}33;
  margin-bottom: ${getSpacing('lg')};
`;

const HeroStats = styled.div`
  display: flex; flex-wrap: wrap;
  justify-content: center;
  gap: ${getSpacing('lg')};
  margin-top: ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    gap: ${getSpacing('md')};
  }
`;

const HeroStat = styled.div`
  display: flex; align-items: center; gap: ${getSpacing('sm')};
  color: rgba(255,255,255,0.9);
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 500;
  svg { width: 16px; height: 16px; opacity: 0.8; }
  .value { font-weight: 600; }
`;

// ─── Sticky Stats Bar ─────────────────────────────────────────────────────────

const StatsBar = styled.div`
  background: ${getColor('neutral.white')};
  padding: ${getSpacing('md')} 0;
  position: sticky;
  top: 56px;
  z-index: ${DesignSystem.zIndex.sticky};
  border-bottom: 1px solid transparent;
  background-image: linear-gradient(${getColor('neutral.white')}, ${getColor('neutral.white')}), linear-gradient(90deg, transparent 0%, #CAB96E 50%, transparent 100%);
  background-origin: padding-box, border-box;
  background-clip: padding-box, border-box;
  @media (max-width: ${DesignSystem.breakpoints.md}) { top: 56px; }
`;

const StatsInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
  display: flex; align-items: center; justify-content: center;
  gap: ${getSpacing('2xl')}; flex-wrap: wrap;
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    gap: ${getSpacing('lg')};
    padding: 0 ${getSpacing('lg')};
  }
`;

const StatItem = styled.div`
  display: flex; align-items: center; gap: ${getSpacing('sm')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.700')}; font-weight: 500;
  svg { width: 16px; height: 16px; color: ${getColor('primary.500')}; }
  .stat-value { font-weight: 600; color: ${getColor('primary.900')}; }
`;

// ─── Mobile Day Nav ───────────────────────────────────────────────────────────

const MobileDayNav = styled.div`
  display: none;
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    display: block;
    position: sticky; top: 108px; z-index: ${DesignSystem.zIndex.sticky};
    background: ${getColor('neutral.white')};
    border-bottom: 1px solid ${getColor('primary.200')};
    padding: ${getSpacing('sm')} 0;
  }
`;

const MobileDayScroller = styled.div`
  display: flex; gap: ${getSpacing('sm')};
  overflow-x: auto;
  padding: ${getSpacing('xs')} ${getSpacing('lg')};
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const MobileDayChip = styled.button<{ isActive: boolean }>`
  flex-shrink: 0; scroll-snap-align: start;
  padding: ${getSpacing('xs')} ${getSpacing('lg')};
  border-radius: ${DesignSystem.borderRadius.full};
  border: 1px solid ${(p: any) => p.isActive ? getColor('primary.700') : getColor('primary.300')};
  background: ${(p: any) => p.isActive ? getColor('primary.700') : 'transparent'};
  color: ${(p: any) => p.isActive ? '#fff' : getColor('primary.700')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer; white-space: nowrap;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  &:hover {
    border-color: ${getColor('primary.700')};
    background: ${(p: any) => p.isActive ? getColor('primary.700') : getColor('primary.100')};
  }
`;

// ─── Main Layout ──────────────────────────────────────────────────────────────

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: ${getSpacing('xl')};
  padding: ${getSpacing('xl')} ${getSpacing('lg')};
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: ${DesignSystem.breakpoints.lg}) {
    grid-template-columns: 220px 1fr;
    gap: ${getSpacing('lg')};
  }
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${getSpacing('md')};
    padding: ${getSpacing('lg')} ${getSpacing('md')};
  }
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const DaysSidebar = styled.div`
  position: sticky; top: 126px; height: fit-content;
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius.xl};
  border: 1px solid ${getColor('primary.200')};
  padding: ${getSpacing('lg')};
  box-shadow: ${getShadow('subtle')};
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  @media (max-width: ${DesignSystem.breakpoints.md}) { display: none; }
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${getColor('primary.300')}; border-radius: 2px; }
`;

const SidebarTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: 600;
  color: ${getColor('primary.800')};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0 0 ${getSpacing('md')};
  padding-bottom: ${getSpacing('md')};
  border-bottom: 1px solid ${getColor('primary.200')};
`;

const DaysList = styled.div`
  display: flex; flex-direction: column;
  gap: ${getSpacing('xs')};
  position: relative;
  &::before {
    content: '';
    position: absolute;
    left: 14px; top: 12px; bottom: 12px;
    width: 2px;
    background: ${getColor('primary.200')};
    border-radius: 1px;
  }
`;

const DayNavItem = styled.button<{ isActive: boolean }>`
  width: 100%; text-align: left;
  padding: ${getSpacing('sm')} ${getSpacing('sm')} ${getSpacing('sm')} ${getSpacing('2xl')};
  border: none;
  background: ${(p: any) => p.isActive ? getColor('primary.50') : 'transparent'};
  border-radius: ${DesignSystem.borderRadius.md};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 10px; top: 50%;
    width: 10px; height: 10px;
    border-radius: 50%;
    background: ${(p: any) => p.isActive ? '#CAB96E' : getColor('neutral.white')};
    border: 2px solid ${(p: any) => p.isActive ? '#CAB96E' : getColor('primary.400')};
    transform: translateY(-50%);
    transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
    z-index: 1;
  }

  &:hover { background: ${getColor('primary.50')}; }
`;

const DayNavLabel = styled.span<{ isActive: boolean }>`
  display: block;
  font-size: ${DesignSystem.typography.scale.caption.size};
  font-weight: ${(p: any) => p.isActive ? 600 : 500};
  color: ${(p: any) => p.isActive ? '#CAB96E' : getColor('primary.800')};
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const DayNavSub = styled.span`
  display: block;
  font-size: 0.65rem;
  color: ${getColor('primary.700')};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Content Area ─────────────────────────────────────────────────────────────

const ContentArea = styled.div`
  min-width: 0;
`;

// ─── Section Styles ───────────────────────────────────────────────────────────

const SectionLabel = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #CAB96E;
  margin-bottom: ${getSpacing('sm')};
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.35rem, 2.25vw, ${DesignSystem.typography.scale.h3.size});
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 ${getSpacing('lg')};
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  font-style: italic;
  text-transform: uppercase;
  letter-spacing: -0.02em;
`;

const SectionDivider = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${getSpacing('xl')} 0;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 240px;
    max-width: 60%;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, #CAB96E 50%, transparent 100%);
  }

  &::after {
    content: '◆';
    position: relative;
    z-index: 1;
    color: #CAB96E;
    font-size: 0.5rem;
    opacity: 0.7;
    background: ${getColor('neutral.white')};
    padding: 0 ${getSpacing('md')};
  }
`;

// ─── Highlights ───────────────────────────────────────────────────────────────

const TrekInfoSection = styled.div`
  padding: 0;
`;

const TrekInfoSectionTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.h5.size};
  font-weight: ${DesignSystem.typography.scale.h5.weight};
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')} 0;
  display: flex;
  align-items: center;
  gap: ${getSpacing('sm')};
  svg { width: 20px; height: 20px; color: ${getColor('primary.600')}; }
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('xs')};
`;

const HighlightItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacing('sm')};
  padding: ${getSpacing('xs')} 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  svg { width: 16px; height: 16px; color: #CAB96E; flex-shrink: 0; margin-top: 2px; }
`;

// ─── Day-by-Day Itinerary ─────────────────────────────────────────────────────

const DayCard = styled.div<{ isOpen: boolean }>`
  background: ${getColor('neutral.white')};
  border: 1px solid ${(p: any) => p.isOpen ? getColor('primary.400') : getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.xl};
  overflow: hidden;
  margin-bottom: ${getSpacing('lg')};
  box-shadow: ${(p: any) => p.isOpen ? getShadow('soft') : getShadow('subtle')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
`;

const DayHeader = styled.button`
  width: 100%;
  padding: ${getSpacing('lg')} ${getSpacing('xl')};
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex; align-items: center; gap: ${getSpacing('lg')};
  text-align: left;
  font-family: ${DesignSystem.typography.fonts.primary};
  transition: background ${DesignSystem.motion.duration.fast};
  &:hover { background: ${getColor('primary.50')}; }
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    padding: ${getSpacing('md')} ${getSpacing('lg')};
    gap: ${getSpacing('md')};
  }
`;

const DayNumber = styled.div<{ isOpen: boolean }>`
  flex-shrink: 0;
  width: 48px; height: 48px;
  border-radius: ${DesignSystem.borderRadius.lg};
  background: ${(p: any) => p.isOpen
    ? `linear-gradient(135deg, ${getColor('primary.700')}, ${getColor('primary.600')})`
    : getColor('primary.100')};
  color: ${(p: any) => p.isOpen ? '#fff' : getColor('primary.700')};
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  font-weight: 600;
  line-height: 1;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    width: 40px; height: 40px;
  }
`;

const DayNum = styled.span`
  font-size: 1.1rem;
`;

const DayLabel = styled.span`
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const DayHeaderInfo = styled.div`
  flex: 1; min-width: 0;
`;

const DayTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.h5.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 4px;
  font-family: ${DesignSystem.typography.fonts.primary};
`;

const DayMeta = styled.div`
  display: flex; flex-wrap: wrap; gap: ${getSpacing('md')};
  font-size: ${DesignSystem.typography.scale.caption.size};
  color: ${getColor('primary.700')};
`;

const DayMetaItem = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  svg { width: 12px; height: 12px; }
`;

const DayChevron = styled.div<{ isOpen: boolean }>`
  flex-shrink: 0;
  color: ${getColor('primary.700')};
  transform: rotate(${(p: any) => p.isOpen ? '180deg' : '0deg'});
  transition: transform ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
`;

const DayBody = styled.div<{ isOpen: boolean }>`
  max-height: ${(p: any) => p.isOpen ? '2000px' : '0'};
  opacity: ${(p: any) => p.isOpen ? 1 : 0};
  overflow: hidden;
  transition: max-height ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.ease},
              opacity ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
`;

const DayBodyContent = styled.div`
  padding: 0 ${getSpacing('xl')} ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.md}) {
    padding: 0 ${getSpacing('lg')} ${getSpacing('lg')};
  }
`;



const DayDescription = styled.p`
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  margin: 0 0 ${getSpacing('lg')};
`;

// ─── Altitude Profile Bar ─────────────────────────────────────────────────────

const AltitudeBar = styled.div`
  display: flex; align-items: flex-end; gap: 4px;
  height: 60px;
  margin-bottom: ${getSpacing('lg')};
  padding: ${getSpacing('sm')} ${getSpacing('md')};
  background: ${getColor('primary.50')};
  border-radius: ${DesignSystem.borderRadius.lg};
  border: 1px solid ${getColor('primary.200')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    height: 40px;
  }
`;

const AltitudeSegment = styled.div<{ heightPct: number; isTopPeak?: boolean; isActive: boolean; color: string }>`
  flex: 1;
  height: ${(p: any) => Math.max(p.heightPct, 10)}%;
  background: ${(p: any) => p.isTopPeak ? '#CAB96E' : '#DFD5A8'};
  border: 1px solid ${(p: any) => p.isTopPeak ? '#CAB96E' : '#D4CA96'};
  border-bottom: none;
  border-radius: 3px 3px 0 0;
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  position: relative;
  &:hover {
    opacity: 0.8;
    &::after {
      content: attr(data-alt);
      position: absolute;
      bottom: 105%;
      left: 50%;
      transform: translateX(-50%);
      font-size: 0.6rem;
      color: ${getColor('primary.700')};
      white-space: nowrap;
      background: #fff;
      padding: 2px 6px;
      border-radius: 4px;
      box-shadow: ${getShadow('subtle')};
    }
  }
`;

// ─── Day Highlights / Challenges List ─────────────────────────────────────────

const DayListGroup = styled.div`
  display: flex; flex-direction: column; gap: ${getSpacing('xs')};
  margin-bottom: ${getSpacing('md')};
`;

const DayListItem = styled.div<{ variant?: 'highlight' | 'challenge' }>`
  display: flex; align-items: flex-start; gap: ${getSpacing('sm')};
  padding: 4px 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.6;
  svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 3px;
    color: ${(p: any) => p.variant === 'challenge' ? '#8D8148' : '#CAB96E'};
  }
`;

// ─── Info Cards Grid ──────────────────────────────────────────────────────────

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: ${getColor('neutral.white')};
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.xl};
  padding: ${getSpacing('xl')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  &:hover {
    border-color: ${getColor('primary.400')};
    box-shadow: ${getShadow('soft')};
    transform: translateY(-2px);
  }
`;

const InfoCardHeader = styled.div`
  display: flex; align-items: center; gap: ${getSpacing('md')};
  margin-bottom: ${getSpacing('lg')};
`;

const InfoCardIcon = styled.div<{ bg?: string }>`
  width: 44px; height: 44px;
  border-radius: ${DesignSystem.borderRadius.lg};
  background: ${(p: any) => p.bg || getColor('primary.100')};
  display: flex; align-items: center; justify-content: center;
  svg { width: 22px; height: 22px; color: ${getColor('primary.700')}; }
`;

const InfoCardTitle = styled.h3`
  font-size: ${DesignSystem.typography.scale.h5.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0;
`;

const InfoList = styled.ul`
  list-style: none; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: ${getSpacing('sm')};
`;

const InfoListItem = styled.li`
  display: flex; align-items: flex-start; gap: ${getSpacing('sm')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  line-height: 1.5;
  svg { width: 14px; height: 14px; color: ${getColor('primary.700')}; flex-shrink: 0; margin-top: 3px; }
`;

// ─── Equipment Checklist ──────────────────────────────────────────────────────

const EquipmentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const EquipmentColumn = styled.div``;

const EquipmentTitle = styled.h4`
  font-size: ${DesignSystem.typography.scale.h6.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')};
  display: flex; align-items: center; gap: ${getSpacing('sm')};
  svg { width: 18px; height: 18px; color: ${getColor('primary.600')}; }
`;

const ChecklistItem = styled.div`
  display: flex; align-items: center; gap: ${getSpacing('sm')};
  padding: ${getSpacing('sm')} 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  border-bottom: 1px solid ${getColor('primary.100')};
  &:last-child { border-bottom: none; }
  svg { width: 14px; height: 14px; color: #CAB96E; flex-shrink: 0; }
`;

// ─── Safety Guidelines Accordion ──────────────────────────────────────────────

const SafetyCategory = styled.div`
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.lg};
  overflow: hidden;
  margin-bottom: ${getSpacing('md')};
`;

const SafetyCategoryHeader = styled.button`
  width: 100%;
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border: none;
  background: ${getColor('neutral.white')};
  cursor: pointer;
  display: flex; align-items: center; justify-content: space-between;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: ${DesignSystem.typography.scale.body.size};
  font-weight: 600;
  color: ${getColor('primary.800')};
  transition: background ${DesignSystem.motion.duration.fast};
  &:hover { background: ${getColor('primary.50')}; }
`;

const SafetyCategoryBody = styled.div<{ isOpen: boolean }>`
  max-height: ${(p: any) => p.isOpen ? '500px' : '0'};
  opacity: ${(p: any) => p.isOpen ? 1 : 0};
  overflow: hidden;
  transition: max-height ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease},
              opacity ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  padding: ${(p: any) => p.isOpen ? `${getSpacing('md')} ${getSpacing('lg')} ${getSpacing('lg')}` : `0 ${getSpacing('lg')}`};
`;

// ─── Cost Section ─────────────────────────────────────────────────────────────

const CostBanner = styled.div`
  background: linear-gradient(135deg, ${getColor('primary.800')}, ${getColor('primary.700')});
  color: #fff;
  border-radius: ${DesignSystem.borderRadius.xl};
  padding: ${getSpacing('xl')} ${getSpacing('2xl')};
  text-align: center;
  margin-bottom: ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    padding: ${getSpacing('lg')};
  }
`;

const CostPrice = styled.div`
  font-size: ${DesignSystem.typography.scale.h2.size};
  font-weight: 200;
  font-family: ${DesignSystem.typography.fonts.display};
  margin-bottom: ${getSpacing('xs')};
`;

const CostNote = styled.div`
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  opacity: 0.8;
`;

const IncExcGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${getSpacing('xl')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) { grid-template-columns: 1fr; }
`;

const IncExcColumn = styled.div``;

const IncExcTitle = styled.h4`
  font-size: ${DesignSystem.typography.scale.h6.size};
  font-weight: 600;
  color: ${getColor('primary.900')};
  margin: 0 0 ${getSpacing('md')};
`;

const IncExcItem = styled.div<{ type: 'inc' | 'exc' }>`
  display: flex; align-items: flex-start; gap: ${getSpacing('sm')};
  padding: ${getSpacing('sm')} 0;
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.800')};
  svg {
    width: 14px; height: 14px; flex-shrink: 0; margin-top: 3px;
    color: ${(p: any) => p.type === 'inc' ? '#CAB96E' : getColor('semantic.error')};
  }
`;

// ─── View More Toggle ─────────────────────────────────────────────────────────

const ViewMoreToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  background: none;
  border: none;
  color: ${getColor('primary.700')};
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  font-weight: 600;
  font-family: ${DesignSystem.typography.fonts.primary};
  cursor: pointer;
  padding: ${getSpacing('sm')} 0;
  margin-top: ${getSpacing('sm')};
  transition: color ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};

  &:hover {
    color: ${getColor('primary.900')};
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  }
`;

// ─── CTA Section ──────────────────────────────────────────────────────────────

const CTASection = styled.div`
  background: ${getColor('neutral.white')};
  padding: 48px 32px;
  text-align: center;
  margin: ${getSpacing('2xl')} 0;

  @media (max-width: 768px) {
    padding: 36px 24px;
  }
  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const CTATitle = styled.h2`
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 500;
  color: #CAB96E;
  margin: 0 0 ${getSpacing('sm')} 0;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
  letter-spacing: -0.02em;
  font-style: italic;
  line-height: 1.3;
  text-transform: uppercase;
`;

const CTADescription = styled.p`
  font-size: ${DesignSystem.typography.scale.bodyLarge.size};
  color: ${getColor('primary.600')};
  margin: 0 0 ${getSpacing('xl')};
  max-width: 600px;
  margin-left: auto; margin-right: auto;
`;

const CTAButton = styled.button`
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: #CAB96E;
  border-radius: 50px;
  border: 1px solid #CAB96E;
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(101, 90, 71, 0.2);
  transition: all 0.3s ease;
  &:hover {
    background: #b8a75e;
    border-color: #b8a75e;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 20px rgba(101, 90, 71, 0.3);
  }
  &:active { transform: translateY(0) scale(0.98); }
`;

// ─── Best Season ──────────────────────────────────────────────────────────────

const SeasonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 4px;
  margin-top: ${getSpacing('md')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const MonthCell = styled.div<{ isActive: boolean }>`
  text-align: center;
  padding: ${getSpacing('sm')} ${getSpacing('xs')};
  border-radius: ${DesignSystem.borderRadius.md};
  background: ${(p: any) => p.isActive
    ? `linear-gradient(135deg, ${getColor('semantic.success')}20, ${getColor('semantic.success')}30)`
    : getColor('primary.50')};
  border: 1px solid ${(p: any) => p.isActive ? getColor('semantic.success') : getColor('primary.200')};
  font-size: 0.65rem;
  font-weight: 600;
  color: ${(p: any) => p.isActive ? getColor('semantic.success') : getColor('primary.400')};
  @media (max-width: ${DesignSystem.breakpoints.sm}) {
    font-size: 0.55rem;
    padding: ${getSpacing('xs')};
  }
`;

// ─── Loading ──────────────────────────────────────────────────────────────────

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: ${getSpacing('xl')};
  background: ${getColor('primary.50')};
`;

const LoadingBar = styled.div`
  width: 200px; height: 3px;
  background: ${getColor('primary.200')};
  border-radius: 2px;
  overflow: hidden;
  &::after {
    content: '';
    display: block;
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg, ${getColor('primary.700')}, ${getColor('primary.500')});
    border-radius: 2px;
    animation: ${shimmer} 1.5s ease-in-out infinite;
    background-size: 200% 100%;
  }
`;

const LoadingText = styled.p`
  font-size: ${DesignSystem.typography.scale.bodySmall.size};
  color: ${getColor('primary.500')};
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: ${getSpacing('xl')};
  text-align: center;
  padding: ${getSpacing('xl')};
`;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const TrekDetailPage: React.FC<TrekDetailPageProps> = ({ trekId, state, onBack, onNavigateTo }) => {
  const [trek, setTrek] = useState<TrekDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDays, setOpenDays] = useState<Set<number>>(new Set());
  const [showBooking, setShowBooking] = useState(false);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [showAllProvided, setShowAllProvided] = useState(false);
  const [showAllPersonal, setShowAllPersonal] = useState(false);
  const [showAllInclusions, setShowAllInclusions] = useState(false);
  const [showAllExclusions, setShowAllExclusions] = useState(false);

  // ─── Scroll to top on mount ────────────────────────────────────────────────
  useEffect(() => { window.scrollTo(0, 0); }, [trekId, state]);

  // ─── BookingForm history integration ──────────────────────────────────────
  const openBookingForm = useCallback(() => {
    setIsEnquiryOpen(true);
    window.history.pushState(
      { page: 'trek-detail', data: { itineraryId: trekId, state }, bookingOpen: true },
      '',
      window.location.href
    );
  }, [trekId, state]);

  useEffect(() => {
    const handlePopState = () => {
      if (isEnquiryOpen) {
        setIsEnquiryOpen(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isEnquiryOpen]);

  // ─── Auto-popup EnquiryDialog after 20s (once per session) ────────────────
  useEffect(() => {
    if (sessionStorage.getItem('enquiry_dialog_shown')) return;
    const timer = setTimeout(() => {
      setIsEnquiryOpen(true);
      sessionStorage.setItem('enquiry_dialog_shown', '1');
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  const [openSafety, setOpenSafety] = useState<Set<number>>(new Set([0]));
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  const { progress, showBackToTop } = useScrollProgress();
  const activeDay = useScrollSpy(trek?.days?.length || 0);

  // ─── Fetch Trek Data ─────────────────────────────────────────────────────

  useEffect(() => {
    const fetchTrek = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await ItineraryAPI.getTrekDetails(trekId);
        setTrek(data);
      } catch (err: any) {
        console.error('Failed to fetch trek details:', err);
        setError(err.message || 'Failed to load trek details');
      } finally {
        setLoading(false);
      }
    };
    if (trekId) fetchTrek();
  }, [trekId]);



  // ─── Handlers ────────────────────────────────────────────────────────────

  const toggleDay = useCallback((dayNum: number) => {
    setOpenDays(prev => {
      const next = new Set(prev);
      if (next.has(dayNum)) next.delete(dayNum); else next.add(dayNum);
      return next;
    });
  }, []);

  const toggleSafety = useCallback((idx: number) => {
    setOpenSafety(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const scrollToDay = useCallback((dayNum: number) => {
    const el = document.getElementById(`trek-day-${dayNum}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setOpenDays(prev => new Set(prev).add(dayNum));
    }
  }, []);

  // ─── Altitude Profile Helpers ────────────────────────────────────────────

  const getAltitudeProfile = useCallback(() => {
    if (!trek?.days) return [];
    const maxAlt = Math.max(...trek.days.map(d => d.altitude?.end || d.altitude?.peak || 0), 1);
    const altitudes = trek.days.map(d => d.altitude?.peak || d.altitude?.end || 0);
    const sorted = [...altitudes].sort((a, b) => b - a);
    const top2Threshold = sorted.length >= 2 ? sorted[1] : sorted[0] || 0;
    return trek.days.map(d => {
      const peak = d.altitude?.peak || d.altitude?.end || 0;
      return {
        day: d.day,
        altitude: d.altitude?.end || 0,
        peak,
        heightPct: ((d.altitude?.end || 0) / maxAlt) * 100,
        label: `${d.altitude?.end || 0}m`,
        isTopPeak: peak >= top2Threshold && peak > 0,
      };
    });
  }, [trek]);

  // ─── Months helper ───────────────────────────────────────────────────────

  const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const isSeasonMonth = useCallback((month: string) => {
    if (!trek?.bestTimeToVisit) return false;
    const btv = typeof trek.bestTimeToVisit === 'string'
      ? trek.bestTimeToVisit.toLowerCase()
      : (trek.bestTimeToVisit as any)?.primarySeason?.toLowerCase() || '';
    return btv.includes(month.toLowerCase().slice(0, 3));
  }, [trek]);

  // ─── Loading / Error ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <LoadingContainer>
        <Mountain size={48} color={getColor('primary.500')} />
        <LoadingBar />
        <LoadingText>Preparing your trek details…</LoadingText>
      </LoadingContainer>
    );
  }

  if (error || !trek) {
    return (
      <ErrorContainer>
        <AlertTriangle size={48} color={getColor('semantic.error')} />
        <h2 style={{ color: getColor('primary.900'), fontFamily: DesignSystem.typography.fonts.display }}>
          Unable to Load Trek
        </h2>
        <p style={{ color: getColor('primary.800') }}>{error || 'Trek not found'}</p>
        <CTAButton onClick={onBack}>← Go Back</CTAButton>
      </ErrorContainer>
    );
  }

  const altProfile = getAltitudeProfile();
  const heroImage = trek.landingImage
    ? `${API_BASE_URL}/api/images/${trek.landingImage}`
    : JOURNEY_IMAGES[Math.abs(trekId.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % JOURNEY_IMAGES.length];

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <PageContainer>
      <ProgressBar width={progress} />
      <AppHeader 
        onNavigateTo={onNavigateTo || (() => {})} 
        transparent
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <HeroSection>
        <HeroBg bgImage={heroImage} />
        <HeroContent>
          <DifficultyBadge difficulty={trek.difficulty || ''}>
            <Mountain size={12} />
            {trek.difficulty || 'Moderate'}
          </DifficultyBadge>

          <HeroTagline>{trek.region || trek.state?.[0] || 'Himalayas'}</HeroTagline>

          <HeroTitle>{trek.name}</HeroTitle>

          {trek.description && (
            <HeroOverview>{trek.description}</HeroOverview>
          )}

          <HeroStats>
            <HeroStat>
              <Calendar size={16} />
              <span className="value">{trek.duration}</span> Days
            </HeroStat>
            <HeroStat>
              <Route size={16} />
              <span className="value">{trek.totalDistance}</span>
            </HeroStat>
            <HeroStat>
              <Mountain size={16} />
              <span className="value">{trek.maxAltitude?.toLocaleString()}m</span>
            </HeroStat>
            <HeroStat>
              <TrendingUp size={16} />
              <span className="value">↑{trek.elevationGain?.toLocaleString()}m</span>
            </HeroStat>
            {trek.bestTimeToVisit && (
              <HeroStat>
                <Sun size={16} />
                <span className="value">{typeof trek.bestTimeToVisit === 'string' ? trek.bestTimeToVisit : (trek.bestTimeToVisit as any)?.primarySeason || 'Seasonal'}</span>
              </HeroStat>
            )}
          </HeroStats>

          <HeroCTAButton onClick={openBookingForm}>Book This Trek</HeroCTAButton>
        </HeroContent>
      </HeroSection>

      {/* ── Sticky Stats ─────────────────────────────────────────────────── */}
      <StatsBar>
        <StatsInner>
          <StatItem>
            <Calendar size={16} />
            <span className="stat-value">{trek.duration}D</span>
          </StatItem>
          <StatItem>
            <Route size={16} />
            <span className="stat-value">{trek.totalDistance}</span>
          </StatItem>
          <StatItem>
            <Mountain size={16} />
            <span className="stat-value">{trek.maxAltitude?.toLocaleString()}m peak</span>
          </StatItem>
          <StatItem>
            <TrendingUp size={16} />
            <span className="stat-value">↑{trek.elevationGain?.toLocaleString()}m gain</span>
          </StatItem>
          <StatItem>
            <Star size={16} />
            <span className="stat-value" style={{ color: getDifficultyColor(trek.difficulty || '') }}>
              {trek.difficulty}
            </span>
          </StatItem>
        </StatsInner>
      </StatsBar>

      {/* ── Mobile Day Nav ───────────────────────────────────────────────── */}
      <MobileDayNav>
        <MobileDayScroller>
          {(trek.days || []).map(d => (
            <MobileDayChip
              key={d.day}
              isActive={activeDay === d.day}
              onClick={() => scrollToDay(d.day)}
            >
              Day {d.day}
            </MobileDayChip>
          ))}
        </MobileDayScroller>
      </MobileDayNav>

      {/* ── Main Layout ──────────────────────────────────────────────────── */}
      <MainLayout>
        {/* Sidebar */}
        <DaysSidebar>
          <SidebarTitle>Trek Itinerary</SidebarTitle>
          <DaysList>
            {(trek.days || []).map(d => (
              <DayNavItem
                key={d.day}
                isActive={activeDay === d.day}
                onClick={() => scrollToDay(d.day)}
              >
                <DayNavLabel isActive={activeDay === d.day}>Day {d.day}</DayNavLabel>
                <DayNavSub>{d.title}</DayNavSub>
              </DayNavItem>
            ))}
          </DaysList>
        </DaysSidebar>

        {/* Content */}
        <ContentArea>

          {/* ── Trip Highlights ───────────────────────────────────────── */}
          {trek.highlights && trek.highlights.length > 0 && (
          <ScrollReveal>
            <TrekInfoSection>
              <TrekInfoSectionTitle><Sparkles /> Trip Highlights</TrekInfoSectionTitle>
              <HighlightsList>
                {trek.highlights.map((h, i) => (
                  <HighlightItem key={i}>
                    <Check />{h}
                  </HighlightItem>
                ))}
              </HighlightsList>
            </TrekInfoSection>
          </ScrollReveal>
          )}

          {/* ── Altitude Profile ───────────────────────────────────────── */}
          {altProfile.length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Altitude Profile</SectionLabel>
              <SectionTitle>Elevation Overview</SectionTitle>
              <AltitudeBar>
                {altProfile.map((seg) => (
                  <AltitudeSegment
                    key={seg.day}
                    heightPct={seg.heightPct}
                    isActive={activeDay === seg.day}
                    isTopPeak={seg.isTopPeak}
                    color={getColor('primary.600')}
                    data-alt={`Day ${seg.day}: ${seg.label}`}
                  />
                ))}
              </AltitudeBar>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: getColor('primary.700') }}>
                {altProfile.map(seg => (
                  <span key={seg.day} style={{ textAlign: 'center', flex: 1 }}>
                    D{seg.day}<br />{seg.label}
                  </span>
                ))}
              </div>
            </ScrollReveal>
          )}

          {/* ── Day-by-Day Itinerary ──────────────────────────────────── */}
          <ScrollReveal delay={150}>
            <SectionDivider />
            <SectionLabel>Day-by-Day</SectionLabel>
            <SectionTitle>Trek Itinerary</SectionTitle>

            {(trek.days || []).map((day, idx) => (
              <div key={day.day} id={`trek-day-${day.day}`} style={{ scrollMarginTop: '160px' }}>
                <DayCard isOpen={openDays.has(day.day)}>
                  <DayHeader onClick={() => toggleDay(day.day)}>
                    <DayNumber isOpen={openDays.has(day.day)}>
                      <DayNum>{day.day}</DayNum>
                      <DayLabel>DAY</DayLabel>
                    </DayNumber>
                    <DayHeaderInfo>
                      <DayTitle>{day.title}</DayTitle>
                      <DayMeta>
                        {day.altitude?.start && day.altitude?.end && (
                          <DayMetaItem>
                            <Mountain />
                            {day.altitude.start}m → {day.altitude.end}m
                            {day.altitude.peak && day.altitude.peak > day.altitude.end && ` (peak: ${day.altitude.peak}m)`}
                          </DayMetaItem>
                        )}
                        {(day.distance ?? 0) > 0 && (
                          <DayMetaItem>
                            <Route /> {day.distance} km
                          </DayMetaItem>
                        )}
                        {day.trekDuration && (
                          <DayMetaItem>
                            <Clock /> {day.trekDuration}
                          </DayMetaItem>
                        )}
                      </DayMeta>
                    </DayHeaderInfo>
                    <DayChevron isOpen={openDays.has(day.day)}>
                      <ChevronDown size={20} />
                    </DayChevron>
                  </DayHeader>

                  <DayBody isOpen={openDays.has(day.day)}>
                    <DayBodyContent>
                      {day.description && (
                        <DayDescription>{day.description}</DayDescription>
                      )}

                      {/* Day highlights */}
                      {day.activities && day.activities.length > 0 && (
                        <>
                          <div style={{ fontSize: DesignSystem.typography.scale.caption.size, fontWeight: 700, color: getColor('primary.800'), marginBottom: getSpacing('sm'), textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Highlights
                          </div>
                          <DayListGroup>
                            {day.activities.slice(0, 3).map((h, i) => (
                              <DayListItem key={i} variant="highlight">
                                <Star size={14} /> {h}
                              </DayListItem>
                            ))}
                          </DayListGroup>
                        </>
                      )}

                      {/* Day challenges */}
                      {day.challenges && day.challenges.length > 0 && (
                        <>
                          <div style={{ fontSize: DesignSystem.typography.scale.caption.size, fontWeight: 700, color: getColor('primary.800'), marginBottom: getSpacing('sm'), textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Challenges
                          </div>
                          <DayListGroup>
                            {day.challenges.slice(0, 3).map((c, i) => (
                              <DayListItem key={i} variant="challenge">
                                <AlertTriangle size={14} /> {c}
                              </DayListItem>
                            ))}
                          </DayListGroup>
                        </>
                      )}
                    </DayBodyContent>
                  </DayBody>
                </DayCard>
              </div>
            ))}
          </ScrollReveal>

          {/* ── Equipment Checklist ────────────────────────────────────── */}
          {trek.equipmentChecklist && Object.keys(trek.equipmentChecklist).length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Gear Up</SectionLabel>
              <SectionTitle>Equipment Checklist</SectionTitle>
              <EquipmentGrid>
                {trek.equipmentChecklist.providedByOperator && (
                  <EquipmentColumn>
                    <EquipmentTitle><Backpack /> Provided by Operator</EquipmentTitle>
                    {(showAllProvided ? trek.equipmentChecklist.providedByOperator : trek.equipmentChecklist.providedByOperator.slice(0, 5)).map((item, i) => (
                      <ChecklistItem key={i}><Check size={14} /> {item}</ChecklistItem>
                    ))}
                    {trek.equipmentChecklist.providedByOperator.length > 5 && (
                      <ViewMoreToggle onClick={() => setShowAllProvided(!showAllProvided)}>
                        {showAllProvided ? <>View Less <ChevronUp size={14} /></> : <>View More ({trek.equipmentChecklist.providedByOperator.length - 5} more) <ChevronDown size={14} /></>}
                      </ViewMoreToggle>
                    )}
                  </EquipmentColumn>
                )}
                {trek.equipmentChecklist.mustBringPersonal && (
                  <EquipmentColumn>
                    <EquipmentTitle><Backpack /> Must Bring (Personal)</EquipmentTitle>
                    {(showAllPersonal ? trek.equipmentChecklist.mustBringPersonal : trek.equipmentChecklist.mustBringPersonal.slice(0, 5)).map((item, i) => (
                      <ChecklistItem key={i}><Check size={14} /> {item}</ChecklistItem>
                    ))}
                    {trek.equipmentChecklist.mustBringPersonal.length > 5 && (
                      <ViewMoreToggle onClick={() => setShowAllPersonal(!showAllPersonal)}>
                        {showAllPersonal ? <>View Less <ChevronUp size={14} /></> : <>View More ({trek.equipmentChecklist.mustBringPersonal.length - 5} more) <ChevronDown size={14} /></>}
                      </ViewMoreToggle>
                    )}
                  </EquipmentColumn>
                )}
              </EquipmentGrid>
            </ScrollReveal>
          )}

          {/* ── Safety Guidelines ─────────────────────────────────────── */}
          {trek.safetyGuidelines && trek.safetyGuidelines.length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Stay Safe</SectionLabel>
              <SectionTitle>Safety Guidelines</SectionTitle>
              {trek.safetyGuidelines.map((cat, idx) => (
                <SafetyCategory key={idx}>
                  <SafetyCategoryHeader onClick={() => toggleSafety(idx)}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: getSpacing('sm') }}>
                      <Shield size={18} color={getColor('primary.600')} />
                      {cat.category}
                    </span>
                    {openSafety.has(idx) ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </SafetyCategoryHeader>
                  <SafetyCategoryBody isOpen={openSafety.has(idx)}>
                    <InfoList>
                      {cat.guidelines.map((g, i) => (
                        <InfoListItem key={i}>
                          <AlertTriangle /> {g}
                        </InfoListItem>
                      ))}
                    </InfoList>
                  </SafetyCategoryBody>
                </SafetyCategory>
              ))}
            </ScrollReveal>
          )}

          {/* ── Required Skills & Medical ─────────────────────────────── */}
          {((trek.requiredSkills?.length ?? 0) > 0 || (trek.medicalPreparation?.length ?? 0) > 0) && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Preparation</SectionLabel>
              <SectionTitle>Skills & Medical</SectionTitle>
              <InfoGrid>
                {trek.requiredSkills && trek.requiredSkills.length > 0 && (
                  <InfoCard>
                    <InfoCardHeader>
                      <InfoCardIcon bg={getColor('primary.100')}><Footprints /></InfoCardIcon>
                      <InfoCardTitle>Required Skills</InfoCardTitle>
                    </InfoCardHeader>
                    <InfoList>
                      {trek.requiredSkills.map((s, i) => (
                        <InfoListItem key={i}><Check /> {s}</InfoListItem>
                      ))}
                    </InfoList>
                  </InfoCard>
                )}
                {trek.medicalPreparation && trek.medicalPreparation.length > 0 && (
                  <InfoCard>
                    <InfoCardHeader>
                      <InfoCardIcon bg="rgba(239,68,68,0.1)"><Heart /></InfoCardIcon>
                      <InfoCardTitle>Medical Preparation</InfoCardTitle>
                    </InfoCardHeader>
                    <InfoList>
                      {trek.medicalPreparation.map((m, i) => (
                        <InfoListItem key={i}><Heart /> {m}</InfoListItem>
                      ))}
                    </InfoList>
                  </InfoCard>
                )}
              </InfoGrid>
            </ScrollReveal>
          )}

          {/* ── Cost Section ──────────────────────────────────────────── */}
          {trek.costConsiderations && Object.keys(trek.costConsiderations).length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Investment</SectionLabel>
              <SectionTitle>Cost & Inclusions</SectionTitle>

              <div style={{
                textAlign: 'center',
                padding: `${getSpacing('lg')} ${getSpacing('xl')}`,
                marginBottom: getSpacing('xl'),
              }}>
                <p style={{
                  fontSize: '1.05rem',
                  fontStyle: 'italic',
                  color: getColor('primary.800'),
                  marginBottom: getSpacing('lg'),
                  lineHeight: 1.7,
                }}>
                  For detailed pricing, please contact our team
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: getSpacing('sm') }}>
                  <a href="tel:+916005365063" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: getColor('primary.700'), textDecoration: 'none', fontSize: '0.95rem', fontWeight: 500 }}>
                    <Phone size={16} /> +91 6005365063
                  </a>
                  <a href="tel:+919419227555" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: getColor('primary.700'), textDecoration: 'none', fontSize: '0.95rem', fontWeight: 500 }}>
                    <Phone size={16} /> +91 9419227555
                  </a>
                </div>
              </div>

              <IncExcGrid>
                {trek.costConsiderations.inclusions && (
                  <IncExcColumn>
                    <IncExcTitle>✓ Inclusions</IncExcTitle>
                    {(showAllInclusions ? trek.costConsiderations.inclusions : trek.costConsiderations.inclusions.slice(0, 5)).map((item, i) => (
                      <IncExcItem key={i} type="inc"><Check /> {item}</IncExcItem>
                    ))}
                    {trek.costConsiderations.inclusions.length > 5 && (
                      <ViewMoreToggle onClick={() => setShowAllInclusions(!showAllInclusions)}>
                        {showAllInclusions ? <>View Less <ChevronUp size={14} /></> : <>View More ({trek.costConsiderations.inclusions.length - 5} more) <ChevronDown size={14} /></>}
                      </ViewMoreToggle>
                    )}
                  </IncExcColumn>
                )}
                {trek.costConsiderations.exclusions && (
                  <IncExcColumn>
                    <IncExcTitle>✕ Exclusions</IncExcTitle>
                    {(showAllExclusions ? trek.costConsiderations.exclusions : trek.costConsiderations.exclusions.slice(0, 5)).map((item, i) => (
                      <IncExcItem key={i} type="exc"><AlertTriangle /> {item}</IncExcItem>
                    ))}
                    {trek.costConsiderations.exclusions.length > 5 && (
                      <ViewMoreToggle onClick={() => setShowAllExclusions(!showAllExclusions)}>
                        {showAllExclusions ? <>View Less <ChevronUp size={14} /></> : <>View More ({trek.costConsiderations.exclusions.length - 5} more) <ChevronDown size={14} /></>}
                      </ViewMoreToggle>
                    )}
                  </IncExcColumn>
                )}
              </IncExcGrid>
            </ScrollReveal>
          )}

          {/* ── Best Season ───────────────────────────────────────────── */}
          {trek.bestTimeToVisit && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>When to Go</SectionLabel>
              <SectionTitle>Best Season</SectionTitle>
              <p style={{ color: getColor('primary.800'), marginBottom: getSpacing('md'), fontSize: DesignSystem.typography.scale.body.size }}>
                Best months to attempt this trek: <strong>{typeof trek.bestTimeToVisit === 'string' ? trek.bestTimeToVisit : (trek.bestTimeToVisit as any)?.primarySeason || 'Seasonal'}</strong>
              </p>
              <SeasonGrid>
                {ALL_MONTHS.map(m => (
                  <MonthCell key={m} isActive={isSeasonMonth(m)}>
                    {m}
                  </MonthCell>
                ))}
              </SeasonGrid>
            </ScrollReveal>
          )}

          {/* ── Community & Culture ────────────────────────────────────── */}
          {trek.communityAndCulture && trek.communityAndCulture.length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Culture</SectionLabel>
              <SectionTitle>Community & Culture</SectionTitle>
              <InfoList>
                {trek.communityAndCulture.map((item, i) => (
                  <InfoListItem key={i}><Users /> {item}</InfoListItem>
                ))}
              </InfoList>
            </ScrollReveal>
          )}

          {/* ── Environmental Considerations ──────────────────────────── */}
          {trek.environmentalConsiderations && trek.environmentalConsiderations.length > 0 && (
            <ScrollReveal delay={100}>
              <SectionDivider />
              <SectionLabel>Responsibility</SectionLabel>
              <SectionTitle>Environmental Guidelines</SectionTitle>
              <InfoList>
                {trek.environmentalConsiderations.map((item, i) => (
                  <InfoListItem key={i}><Leaf /> {item}</InfoListItem>
                ))}
              </InfoList>
            </ScrollReveal>
          )}

          {/* ── CTA ───────────────────────────────────────────────────── */}
          <ScrollReveal delay={100}>
            <CTASection>
              <CTATitle>Ready for {trek.name}?</CTATitle>
              <CTADescription>
                Book your spot now and embark on an unforgettable adventure through the Himalayas.
              </CTADescription>
              <CTAButton onClick={openBookingForm}>
                Book This Trek →
              </CTAButton>
            </CTASection>
          </ScrollReveal>
        </ContentArea>
      </MainLayout>

      {/* ── Back to Top ──────────────────────────────────────────────────── */}
      <BackToTopButton
        visible={showBackToTop}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ArrowUp size={20} />
      </BackToTopButton>

      {/* ── Booking Form Modal ────────────────────────────────────────────── */}
      {showBooking && (
        <BookingForm
          destination={trek ? {
            id: 1,
            title: trek.name,
            state: trek.state?.[0] || state || 'Ladakh',
            duration: `${trek.duration} days`
          } : null}
          isOpen={showBooking}
          onClose={() => setShowBooking(false)}
          onBack={() => setShowBooking(false)}
          onNavigateTo={onNavigateTo}
        />
      )}

      <EnquiryDialog
        isOpen={isEnquiryOpen}
        onClose={() => setIsEnquiryOpen(false)}
        sourcePage="trek-detail"
        itineraryId={trekId}
        itineraryTitle={trek?.name}
      />
    </PageContainer>
  );
};

export default TrekDetailPage;
